import * as React from 'react';
import {
  ALLOWED_EMAIL_DOMAIN,
  canSetPasswordFor,
  companyEmail,
  passwordStrength,
  ROLE_LABELS,
  ROLES,
  type Role,
} from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@/components/cargofl-ui';
import { PasswordInput, PasswordMatch, PasswordStrength } from '@/components/password-fields';
import { ApiError } from '@/lib/api';
import {
  canManageOrgStructure,
  useInlineCreateDepartment,
  useInlineCreateDesignation,
} from '@/lib/org-api';
import {
  flattenDepartments,
  useChangeRole,
  useCreateEmployee,
  useDepartments,
  useDesignations,
  useEmployees,
  useSetPassword,
  useUpdateEmployee,
  type Employee,
  type EmployeeSaveResult,
} from '@/lib/people-api';
import { useAuthConfig, useCurrentUser, useSignOut } from '@/lib/session';

const NONE = '__none__';

/**
 * Say what the job title's KRA group did, if anything.
 *
 * Silent when there was nothing to do — most titles have no group pinned, and a toast
 * for every save would train people to dismiss all of them. Loud in both the other
 * cases: goals that appeared on their own need explaining, and goals that were expected
 * and did not appear need explaining more.
 */
function reportAutoAssign(outcome: EmployeeSaveResult['autoAssigned']) {
  if (!outcome) return;
  if (outcome.assigned) {
    toast.success('Goals assigned from their job title', {
      description: 'A draft goal set is ready on Goal assignments — review it, then publish.',
    });
  } else {
    toast.warning('No goals assigned', { description: outcome.message ?? undefined });
  }
}

/**
 * Onboard or edit an employee. One form for both because the fields are the same
 * set - a separate edit dialog is two places to keep the manager/department rules
 * in step, and they would drift.
 *
 * Day-1 onboarding asks for department and manager up front rather than leaving
 * them to a later edit: an employee without both cannot be assigned goals or
 * reviewed, and creating one and moving on is how people end up stranded in
 * "needs setup".
 */
export function EmployeeDialog({
  employee,
  open,
  onOpenChange,
}: {
  /** Absent = onboard a new employee. */
  employee?: Employee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const me = useCurrentUser();
  const editing = Boolean(employee);

  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [employeeCode, setEmployeeCode] = React.useState('');
  const [role, setRole] = React.useState<Role>('EMPLOYEE');
  const [departmentId, setDepartmentId] = React.useState(NONE);
  const [designationId, setDesignationId] = React.useState(NONE);
  const [managerId, setManagerId] = React.useState(NONE);
  /**
   * The chosen manager's name, remembered separately.
   *
   * The picker only holds the 20 people matching the current search, and the person
   * already chosen is usually not among them — without this the field would go blank
   * the moment the search box was cleared, on an employee whose manager is set.
   */
  const [managerName, setManagerName] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});
  /** Typed into the manager picker. Searched on the server, not in the browser. */
  const [managerSearch, setManagerSearch] = React.useState('');
  /** Password sign-in only. Blank = leave whatever they have; never read back. */
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const { googleLogin, passwordLogin } = useAuthConfig();
  const signOut = useSignOut();
  const setPasswordMutation = useSetPassword();

  const { data: departmentTree } = useDepartments();
  const { data: designations } = useDesignations();
  // Managers come from the full list: being a manager is a fact about the reporting
  // tree, not a role, so filtering to role === MANAGER would hide valid choices.
  //
  // Searched server-side and capped at 20: the picker needs the handful that match
  // what was typed, not the whole company in the browser, and that stays true when
  // the company is 700 people rather than 71.
  const { data: people, isFetching: peopleLoading, error: peopleError } = useEmployees({
    search: managerSearch || undefined,
    pageSize: 20,
  });
  const createDepartment = useInlineCreateDepartment();
  const createDesignation = useInlineCreateDesignation();
  const canEditOrg = canManageOrgStructure(me.role);
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  // Role has its own endpoint because the server also revokes that person's sessions.
  const changeRole = useChangeRole();

  const departments = React.useMemo(() => flattenDepartments(departmentTree ?? []), [departmentTree]);

  // Reload the form whenever it opens, so a cancelled edit does not leak into the
  // next person the user clicks.
  React.useEffect(() => {
    if (!open) return;
    setEmail(employee?.email ?? '');
    setName(employee?.name ?? '');
    setEmployeeCode(employee?.employeeCode ?? '');
    setRole(employee?.role ?? 'EMPLOYEE');
    setDepartmentId(employee?.departmentId ?? NONE);
    setDesignationId(employee?.designationId ?? NONE);
    setManagerId(employee?.reportingManagerId ?? NONE);
    setManagerName(employee?.reportingManagerName ?? null);
    setManagerSearch('');
    setPassword('');
    setConfirmPassword('');
    setFieldErrors({});
  }, [open, employee]);

  /**
   * The field only appears where the API would accept it: password sign-in is on, and
   * (canSetPasswordFor) only a super admin may set an HR or super admin account's - checked
   * against the role they hold now AND the one this save gives them, so promoting someone
   * to HR is not a way round it.
   */
  const targetId = employee?.id ?? '';
  const canSetPassword =
    passwordLogin &&
    canSetPasswordFor(me, { id: targetId, role: employee?.role ?? role }) &&
    canSetPasswordFor(me, { id: targetId, role });

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setFieldErrors({});
    const newPassword = canSetPassword ? password : '';
    if (newPassword) {
      // Checked before anything is saved, so a weak password does not leave half a save behind.
      const problem = passwordStrength(newPassword).problem || (newPassword !== confirmPassword ? "Passwords don't match" : '');
      if (problem) {
        setFieldErrors({ password: [problem] });
        return;
      }
    }
    const departmentIdOrNull = departmentId === NONE ? null : departmentId;
    const designationIdOrNull = designationId === NONE ? null : designationId;
    const managerIdOrNull = managerId === NONE ? null : managerId;

    try {
      if (employee) {
        const saved = await update.mutateAsync({
          id: employee.id,
          name,
          employeeCode,
          departmentId: departmentIdOrNull,
          designationId: designationIdOrNull,
          reportingManagerId: managerIdOrNull,
        });
        // Before any role change: the server checks the role they hold at that moment.
        if (newPassword) await setPasswordMutation.mutateAsync({ id: employee.id, password: newPassword });
        if (role !== employee.role) {
          await changeRole.mutateAsync({ id: employee.id, role });
          toast.success(`${name} updated`, {
            description: `Now ${ROLE_LABELS[role]}. Their existing sessions were signed out.`,
          });
        } else {
          toast.success(`${name} updated`);
        }
        reportAutoAssign(saved.autoAssigned);
        // Setting your own password ended your own sessions too.
        if (newPassword && employee.id === me.id) {
          await signOut('/login?changed=1');
          return;
        }
      } else {
        const added = await create.mutateAsync({
          email,
          name,
          employeeCode,
          role,
          departmentId: departmentIdOrNull,
          designationId: designationIdOrNull,
          reportingManagerId: managerIdOrNull,
        });
        if (newPassword) await setPasswordMutation.mutateAsync({ id: added.id, password: newPassword });
        toast.success(`${added.name} added`, {
          description: `They can sign in with ${added.email}.`,
        });
        reportAutoAssign(added.autoAssigned);
      }
      onOpenChange(false);
    } catch (err) {
      const title = editing ? 'Could not save changes' : 'Could not add employee';
      if (err instanceof ApiError) {
        if (err.fieldErrors) setFieldErrors(err.fieldErrors);
        // The toast's own message is generic when fieldErrors exist ("Some fields
        // need attention"), so surface the first real one - the inline text is
        // below the fold in a scrolled dialog.
        toast.error(title, {
          description: Object.values(err.fieldErrors ?? {})[0]?.[0] ?? err.message,
        });
      } else {
        toast.error(title);
      }
    }
  }

  const fieldError = (key: string) => fieldErrors[key]?.[0];
  const saving =
    create.isPending || update.isPending || changeRole.isPending || setPasswordMutation.isPending;

  const managerOptions = React.useMemo(() => {
    const found = (people?.items ?? [])
      // Nobody reports to themselves, and the server rejects it anyway.
      .filter((p) => p.id !== employee?.id)
      .map((p) => ({
        value: p.id,
        label: p.name,
        description: p.email,
        hint: p.designationTitle ?? undefined,
      }));
    // The current pick is pinned to the top whether or not it matches the search,
    // so the field never reads as empty against a manager that is set.
    const pinned =
      managerId !== NONE && managerName !== null && !found.some((o) => o.value === managerId)
        ? [{ value: managerId, label: managerName }]
        : [];
    return [{ value: NONE, label: 'Not set' }, ...pinned, ...found];
  }, [people, employee?.id, managerId, managerName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={(e) => void submit(e)}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit employee' : 'Onboard employee'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Keep their department, reporting line and role current. Changes take effect immediately.'
                : googleLogin
                  ? `They will be able to sign in with their Google account on the ${ALLOWED_EMAIL_DOMAIN} domain.`
                  : 'Set a password below so they can sign in.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              {/* Gmail-style: type the name, the domain is fixed beside it. The domain is the
                  single most common way this form was wrong (cargofl.com hosts the app and
                  sends the mail, so it reads like the work domain); now it can't be typed at
                  all. The API's refine is still the control. */}
              <div className="flex">
                <Input
                  id="email"
                  value={email.split('@')[0] ?? ''}
                  onChange={(e) => setEmail(companyEmail(e.target.value))}
                  placeholder="firstname"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="rounded-r-none"
                  // The email is the identity Google signs them in with - changing it
                  // would orphan the account, so it is fixed once created.
                  disabled={editing}
                  required
                />
                <span className="inline-flex shrink-0 select-none items-center rounded-r-md border border-l-0 bg-muted px-3 text-sm text-muted-foreground">
                  @{ALLOWED_EMAIL_DOMAIN}
                </span>
              </div>
              {fieldError('email') && (
                <p className="text-xs text-destructive">{fieldError('email')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {fieldError('name') && <p className="text-xs text-destructive">{fieldError('name')}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="employeeCode">Employee ID</Label>
              <Input
                id="employeeCode"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
                placeholder="INN0020"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {/* Deliberately optional, and it says so. A new joiner often signs in
                    before HR has issued a code, and a required field here would mean
                    inventing a placeholder that has to be found and corrected later. */}
                Optional. The ID payroll and the asset tracker use. Leave blank if one
                has not been issued yet.
              </p>
              {fieldError('employeeCode') && (
                <p className="text-xs text-destructive">{fieldError('employeeCode')}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5" data-guide="employee-department">
                <Label htmlFor="departmentPicker">Department</Label>
                <Combobox
                  id="departmentPicker"
                  value={departmentId}
                  onChange={setDepartmentId}
                  options={[
                    { value: NONE, label: 'Not set' },
                    ...departments.map((d) => ({
                      value: d.id,
                      label: d.label,
                      hint: `${d.employeeCount}`,
                    })),
                  ]}
                  searchPlaceholder="Find or create a department"
                  emptyText="No department matches"
                  // Onboarding is where a new team first exists on paper. Sending HR
                  // to Organization and back mid-form is how someone gets filed under
                  // the nearest wrong department instead.
                  onCreate={async (name) => setDepartmentId(await createDepartment(name))}
                  createLabel="Create department"
                  canCreate={canEditOrg}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="designationPicker">Designation</Label>
                <Combobox
                  id="designationPicker"
                  value={designationId}
                  onChange={setDesignationId}
                  options={[
                    { value: NONE, label: 'Not set' },
                    ...(designations ?? []).map((d) => ({
                      value: d.id,
                      label: d.title,
                      hint: `${d.employeeCount}`,
                    })),
                  ]}
                  searchPlaceholder="Find or create a designation"
                  emptyText="No designation matches"
                  onCreate={async (title) => setDesignationId(await createDesignation(title))}
                  createLabel="Create designation"
                  canCreate={canEditOrg}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5" data-guide="employee-manager">
                <Label htmlFor="managerPicker">Reporting manager</Label>
                <Combobox
                  id="managerPicker"
                  value={managerId}
                  // Select-only, and not because of a permission: an employee record
                  // is a Google identity plus a reporting line, and half of one
                  // invented from a dropdown is a person who cannot sign in.
                  onChange={(id) => {
                    setManagerId(id);
                    setManagerName(
                      id === NONE ? null : (managerOptions.find((o) => o.value === id)?.label ?? null),
                    );
                  }}
                  options={managerOptions}
                  onSearchChange={setManagerSearch}
                  loading={peopleLoading}
                  error={peopleError ? 'Could not search people. Try again.' : null}
                  searchPlaceholder="Search by name or email"
                  emptyText="Nobody matches"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select
                  value={role}
                  onValueChange={(v) => setRole(v as Role)}
                  // Changing your own role is how an admin locks themselves out of
                  // the screen they are standing on; the API refuses it too.
                  disabled={editing && employee?.id === me.id}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editing && employee?.id === me.id && (
                  <p className="text-xs text-muted-foreground">
                    Ask another admin to change your own role.
                  </p>
                )}
              </div>
            </div>

            {canSetPassword && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="employee-password">{editing ? 'New password' : 'Password'}</Label>
                  <PasswordInput
                    id="employee-password"
                    autoComplete="new-password"
                    aria-describedby="employee-password-strength"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={editing ? 'Leave blank to keep' : 'Optional'}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="employee-password-confirm">Confirm password</Label>
                  <PasswordInput
                    id="employee-password-confirm"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={!password}
                  />
                  <PasswordMatch password={password} confirm={confirmPassword} />
                </div>
                <div className="sm:col-span-2">
                  <PasswordStrength id="employee-password-strength" value={password} />
                  {fieldError('password') && (
                    <p className="text-xs text-destructive">{fieldError('password')}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    For password sign-in. Saving one signs them out everywhere.
                  </p>
                </div>
              </div>
            )}

            {(departmentId === NONE || managerId === NONE) && (
              <Alert>
                <AlertDescription className="text-xs">
                  Without a department and a reporting manager they cannot be assigned goals, and the
                  manager stage of their assessment would have nobody able to review it. You can set
                  these later, but nothing else can start until you do.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-guide="employee-save" disabled={saving}>
              {saving
                ? editing
                  ? 'Saving…'
                  : 'Adding…'
                : editing
                  ? 'Save changes'
                  : 'Add employee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
